import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'

import { DEFAULT_VIDEO_PLATFORM, VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { Address } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import {
  useBookingDetailsWithModalityOptions,
  VIDEO_PLATFORM_OPTION_VALUE,
} from '../../hooks/useBookingDetailsWithModalityOptions'
import { GetBookingDetailsForClient } from '../../ui-models/clients/CalendarClient'
import { ProviderCalendarEditSessionModalityModal } from '../providerCalendarEditSessionModalityModal/ProviderCalendarEditSessionModalityModal'
import { ScopeSelection } from '../providerCalendarEditSessionModalityScopeContent/ProviderCalendarEditSessionModalityScopeContent'
import { ProviderCalendarEditSessionModalityScopeModal } from '../providerCalendarEditSessionModalityScopeModal/ProviderCalendarEditSessionModalityScopeModal'

export type ProviderCalendarEditSessionModalityProps = {
  showEditModal: boolean
  setShowEditModalityModal: (showModal: boolean) => void
  onRequestClose: () => void
  clientFirstName: string
  clientLastName: string
  clientID: string
  startDate: string
  endDate: string
  timeZone: string
  appointmentId: number
  currentMeetingFormat: MEETING_FORMATS
  currentAppointmentAddress?: string | null
  currentVideoPlatform?: VIDEO_PLATFORM
  bookableInPersonLocations?: Address[] | null
  upcomingAppointmentIds?: number[]
  isLoadingUpcomingAppointmentIds?: boolean
  onUpdateModality?: (
    appointmentIdsToUpdate: number[],
    meetingFormat: MEETING_FORMATS,
    address?: string,
    videoPlatform?: VIDEO_PLATFORM,
  ) => Promise<void>
  getBookingDetailsForClient?: GetBookingDetailsForClient
  splitVideoOptions?: boolean
}

export const ProviderCalendarEditSessionModality: FunctionComponent<ProviderCalendarEditSessionModalityProps> = ({
  showEditModal,
  setShowEditModalityModal,
  onRequestClose,
  clientFirstName,
  clientLastName,
  clientID,
  startDate,
  endDate,
  timeZone,
  appointmentId,
  currentMeetingFormat,
  currentAppointmentAddress,
  currentVideoPlatform,
  bookableInPersonLocations,
  upcomingAppointmentIds,
  isLoadingUpcomingAppointmentIds = false,
  onUpdateModality,
  getBookingDetailsForClient,
  splitVideoOptions,
}) => {
  const hasUpcomingSessions = upcomingAppointmentIds != null && upcomingAppointmentIds.length > 1
  const [showScopeModal, setShowScopeModal] = useState(false)
  const [selectedModality, setSelectedModality] = useState<string>(currentMeetingFormat)
  const [scopeSelection, setScopeSelection] = useState<ScopeSelection>(ScopeSelection.AllSessions)
  const [isSaving, setIsSaving] = useState(false)

  const { isLoadingClientBookingDetails, modalityOptions } = useBookingDetailsWithModalityOptions({
    clientId: clientID,
    enabled: (showEditModal || showScopeModal) && !!clientID,
    getBookingDetailsForClient,
    bookableInPersonLocations,
    splitVideoOptions,
  })

  useEffect(() => {
    if (showEditModal) {
      let resolvedModality: string
      if (currentMeetingFormat === MEETING_FORMATS.IN_PERSON) {
        resolvedModality =
          modalityOptions.find((modalityOption) => (currentAppointmentAddress ?? '').includes(modalityOption.label))
            ?.value ?? modalityOptions[0]?.value
      } else if (currentMeetingFormat === MEETING_FORMATS.VIDEO && splitVideoOptions) {
        resolvedModality = VIDEO_PLATFORM_OPTION_VALUE[currentVideoPlatform ?? DEFAULT_VIDEO_PLATFORM]
      } else {
        resolvedModality = currentMeetingFormat
      }
      setSelectedModality(resolvedModality)
      setScopeSelection(ScopeSelection.AllSessions)
      setShowScopeModal(false)
    }
  }, [
    currentMeetingFormat,
    currentAppointmentAddress,
    currentVideoPlatform,
    splitVideoOptions,
    modalityOptions,
    showEditModal,
  ])

  const getResolvedModality = useCallback(() => {
    const selectedOption = modalityOptions?.find((option) => option.value === selectedModality)
    const meetingFormat = selectedModality.includes(MEETING_FORMATS.IN_PERSON)
      ? MEETING_FORMATS.IN_PERSON
      : selectedOption?.videoPlatform
        ? MEETING_FORMATS.VIDEO
        : (selectedModality as MEETING_FORMATS)
    const address = selectedModality.includes(MEETING_FORMATS.IN_PERSON) ? selectedOption?.address : undefined
    const videoPlatform = selectedOption?.videoPlatform
    return { meetingFormat, address, videoPlatform }
  }, [selectedModality, modalityOptions])

  const handleUpdateSessionsModality = useCallback(() => {
    void (async () => {
      setIsSaving(true)
      try {
        const { meetingFormat, address: selectedAddressOption, videoPlatform } = getResolvedModality()
        await onUpdateModality?.(
          scopeSelection === ScopeSelection.AllSessions ? (upcomingAppointmentIds ?? [appointmentId]) : [appointmentId],
          meetingFormat,
          selectedAddressOption,
          videoPlatform,
        )
        onRequestClose()
      } catch {
        // Error already communicated to the user via toast in onUpdateModality
      } finally {
        setIsSaving(false)
        setShowScopeModal(false)
      }
    })()
  }, [appointmentId, onRequestClose, onUpdateModality, getResolvedModality, scopeSelection, upcomingAppointmentIds])

  const onEditSessionModalityConfirmationPress = useCallback(() => {
    if (hasUpcomingSessions) {
      setShowScopeModal(true)
      setShowEditModalityModal(false)
    } else {
      handleUpdateSessionsModality()
    }
  }, [hasUpcomingSessions, setShowEditModalityModal, handleUpdateSessionsModality])

  return (
    <>
      <ProviderCalendarEditSessionModalityModal
        visible={showEditModal}
        onRequestClose={onRequestClose}
        onConfirmationButtonPress={onEditSessionModalityConfirmationPress}
        confirmButtonLoading={isSaving}
        // Scope (single vs. all upcoming sessions) depends on upcoming IDs, so block confirming until they load
        confirmButtonDisabled={isSaving || isLoadingUpcomingAppointmentIds}
        closeOnScrim={false}
        clientFirstName={clientFirstName}
        clientLastName={clientLastName}
        startDate={startDate}
        endDate={endDate}
        timeZone={timeZone}
        upcomingAppointmentIds={upcomingAppointmentIds}
        selectedModality={selectedModality}
        setSelectedModality={setSelectedModality}
        isLoadingClientBookingDetails={isLoadingClientBookingDetails}
        modalityOptions={modalityOptions}
      />
      <ProviderCalendarEditSessionModalityScopeModal
        visible={showScopeModal}
        onRequestClose={() => setShowScopeModal(false)}
        onConfirmationButtonPress={handleUpdateSessionsModality}
        confirmButtonLoading={isSaving}
        confirmButtonDisabled={isSaving}
        upcomingAppointmentsCount={upcomingAppointmentIds?.length ?? 0}
        scopeSelection={scopeSelection}
        setScopeSelection={setScopeSelection}
      />
    </>
  )
}
